import React from 'react';
import { createApolloClient, setDataFromTreeToCache } from 'lib/apolloClient';
import { PageWrapper, SeoHead } from 'layouts';
import { Category } from 'components';
import { ParsedUrlQuery } from 'querystring';
import { GetStaticProps, NextPage } from 'next';
import {
  CategoryQuery,
  CategoryQueryVariables,
  FullCategoryNodeFragment,
} from 'types';
import { GET_CATEGORY } from 'api/category/queries';
import { CategoryUrls, getCategoryTranslations, logger } from 'utils';
import { EventIds } from 'lib/EventIds';

export type CategoryPageProps = {
  data: FullCategoryNodeFragment | null;
};

const CategoryPage: NextPage<CategoryPageProps> = ({ data }) => {
  const translations = getCategoryTranslations(data?.slug || '');
  return (
    <PageWrapper translations={translations} headerColor="white">
      <SeoHead seoHead={data?.seo?.fullHead} />
      <Category data={data} />
    </PageWrapper>
  );
};

export async function getStaticPaths() {
  return {
    // paths: CategoryUrls,
    // TODO: bring back when Apollo is using GET requests
    paths: [],
    fallback: 'blocking',
  };
}

export const getCategoryPageData = async (
  category: string,
  locale: string | undefined
) => {
  // Create new apollo client instead of calling `initializeApolloClient` which returns a shared one.
  // We do that to avoid caching all posts data in apollo cache, which is then sent to client
  const apolloClient = createApolloClient();

  const { data: categoryData, error } = await apolloClient.query<
    CategoryQuery,
    CategoryQueryVariables
  >({
    query: GET_CATEGORY,
    variables: {
      slug: category,
      wpmlLanguage: locale,
    },
    errorPolicy: 'all',
  });

  const data =
    categoryData?.categories?.edges && categoryData.categories.edges.length > 0
      ? categoryData?.categories?.edges[0]?.node || null
      : null;

  return {
    data,
    error,
  };
};

export interface PageParams extends ParsedUrlQuery {
  category: string;
}

export const getStaticProps: GetStaticProps<CategoryPageProps, PageParams> =
  async (props) => {
    // Use will be redirected to homepage automatically if category param not present
    const category = props.params?.category as string;

    const { data, error } = await getCategoryPageData(category, props.locale);

    if (data !== null && error) {
      logger.error(
        'Getting error on Category post fetch',
        EventIds.CategoryPageError,
        error
      );
    }

    if (data === null) {
      return {
        redirect: {
          destination: props.locale !== 'en' ? `/${props.locale}` : '/',
          permanent: true,
        },
      };
    }
    const dataFromTreeToCacheFn = setDataFromTreeToCache(CategoryPage, {
      componentProps: {
        data,
      },
    });
    const propsResult = await dataFromTreeToCacheFn(props);

    propsResult.props = {
      ...propsResult.props,
      data,
    };
    return propsResult;
  };

export default CategoryPage;
